import {
  Box,
  Card,
  CardContent,
  CardHeader,
  makeStyles,
  MenuItem,
  Select,
  TextField,
} from "@material-ui/core";
import React, { useState } from "react";
import { useIntl } from "react-intl";
import {
  ExamSessionCandidateExamState,
  ExamSessionCandidateExamViewModel,
} from "../../../model/examSessionCandidate/ExamSessionCandidateExamViewModel";
import { ExamSessionCandidateViewModel } from "../../../model/examSessionCandidate/ExamSessionCandidateViewModel";
import { ExamSessionViewModel } from "../../../model/ExamSessionViewModel";
import { GetTranslatedValueIntl } from "../../../model/Shared/TranslatedValue";
import { Book, EventBusy } from "@material-ui/icons";
import { ExamSessionCandidateModuleViewModel } from "../../../model/examSessionCandidate/ExamSessionCandidateModuleViewModel";
import ExamSessionCandidateExam from "./ExamSessionCandidateExam";
import CustomButton from "../../common/buttons/CustomButton";
import TranslatedMessage from "../../translations/TranslatedMessage";
import CustomDraggableDialog from "../../common/customDialog/CustomDraggableDialog";
import { CandidateModuleState } from "../../../model/examSessionCandidate/CandidateModuleState";
import TitleWithIcon from "../../common/TitleWithIcon";
import { GetTranslation } from "../../../translations/GetTranslation";
import ExamSessionCandidateModuleRebookDialog from "./ExamSessionCandidateModuleRebookDialog";
import Alert from "@material-ui/lab/Alert";

const useStyles = makeStyles((theme) => ({
  labelContainer: {
    display: "flex",
    alignItems: "center",
  },
  editExamStateButtonContainer: {
    marginLeft: 8,
  },
  stateSelect: {
    minWidth: 150,
  },
  stateLabel: {
    display: "inline",
  },
  moduleContainer: {
    marginBottom: "1rem",
    padding: theme.spacing(2),
    margin: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    width: "100%",
    gap: theme.spacing(2) + "px",
  },
  moduleTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
  },
  moduleCancel: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1) + "px",
  },
  radio: {
    marginLeft: theme.spacing(4),
    textTransform: "capitalize",
  },
  examSelect: {
    width: theme.spacing(40) + "px",
  },
}));

export interface ExamSessionCandidateModuleCallbacks {
  toggleExamManualState: (moduleId: string, examId: string) => void;
  manualExamStateChange: (moduleId: string, examId: string, state: ExamSessionCandidateExamState) => void;
  examScoreChange: (moduleId: string, examId: string, score?: number | null) => void;
  examGradeChange: (moduleId: string, examId: string, grade?: number | null) => void;
}

interface ExamSessionCandidateModuleProps extends ExamSessionCandidateModuleCallbacks {
  module: ExamSessionCandidateModuleViewModel;
  exams: ExamSessionCandidateExamViewModel[];
  examSession: ExamSessionViewModel;
  examSessionCandidate: ExamSessionCandidateViewModel;
  onCancelModule: (module: ExamSessionCandidateModuleViewModel) => void;
  onRebookModule: (module: ExamSessionCandidateModuleViewModel, rebookWithInvoice: boolean) => void;
  onAddExam: (moduleId: string, examId: string) => void;
}

const ExamSessionCandidateModule: React.FC<ExamSessionCandidateModuleProps> = ({
  module,
  exams,
  examSession,
  examSessionCandidate,
  onCancelModule,
  onRebookModule,
  onAddExam,
  ...callbacks
}) => {
  const classes = useStyles();
  const intl = useIntl();
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [comments, setComments] = useState("");
  const [openRebookDialog, setOpenRebookDialog] = useState(false);

  const handleCancelModule = () => {
    setShowCancelDialog(false);
    const moduleToCancel: ExamSessionCandidateModuleViewModel = {
      ...module,
      comments,
    };
    onCancelModule(moduleToCancel);
  };

  if (module === undefined) {
    return null;
  }

  const pendingExamsToBook = examSession.exams
  .filter((e) => e.includedInModules.de.includes(module.name.de) && exams.filter(ce => ce.examId === e.id).length === 0);

  return (
    <>
      <Card>
        <CardHeader
          title={
            <div className={classes.moduleTitle}>
              <TitleWithIcon title={GetTranslatedValueIntl(intl, module.name)} variant="h5">
                <Book fontSize="small" />
              </TitleWithIcon>
              {module.state === CandidateModuleState.Cancelled && (
                <>
                  <span>
                    <TranslatedMessage id="common.status.cancelled" />
                  </span>
                  <CustomButton
                    tooltip={<TranslatedMessage id="examSessionCandidate.rebook" />}
                    onClick={() => setOpenRebookDialog(true)}
                  >
                    <TranslatedMessage id="examSessionCandidate.rebook" />
                  </CustomButton>
                </>
              )}
            </div>
          }
          action={
            module.state !== CandidateModuleState.Cancelled ? (
              <CustomButton
                tooltip={<TranslatedMessage id="examSessionCandidate.cancel" />}
                onClick={() => setShowCancelDialog(true)}
              >
                <EventBusy />
              </CustomButton>
            ) : (
              <></>
            )
          }
        />
        <CardContent className={classes.moduleContainer}>
          <>
            {exams.map((candidateExam) => (
              <ExamSessionCandidateExam
                key={candidateExam.examId}
                examSessionCandidate={examSessionCandidate}
                examSession={examSession}
                candidateExam={candidateExam}
                {...callbacks}
              />
            ))}
            {pendingExamsToBook.length > 0 && (
              <>
                {module.state === CandidateModuleState.Cancelled && (
                  <Alert severity="info">
                    <TranslatedMessage id="examSessionCandidate.bookingAvailableWhenRebooked" />
                  </Alert>
                )}
                {module.state === CandidateModuleState.ApplicationAccepted && (
                  <Box>
                    <Select
                      displayEmpty={true}
                      className={classes.examSelect}
                      value={""}
                      onChange={(event: React.ChangeEvent<{ value: unknown }>) =>
                        onAddExam(module.moduleId, event.target.value as string)
                      }
                    >
                      <MenuItem key={0} value="" selected>
                        <TranslatedMessage id="examSessionCandidate.selectExamToRebook" />
                      </MenuItem>
                      {pendingExamsToBook.map((exam) => (
                          <MenuItem key={exam.id} value={exam.id}>
                            <span>{GetTranslatedValueIntl(intl, exam.name)}</span>
                          </MenuItem>
                        ))}
                    </Select>
                  </Box>
                )}
              </>
            )}
          </>
        </CardContent>
      </Card>
      {showCancelDialog && (
        <CustomDraggableDialog
          title={<TranslatedMessage id="selfcancellation.header" />}
          content={
            <div className={classes.moduleCancel}>
             <div>
                <TextField
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={5}
                  label={GetTranslation(intl, "cancellationTaskForm.commentsLabel")}
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                />
              </div>
              <div>
                <TranslatedMessage id="examSessionCandidate.cancelModuleMessage" />
              </div>
            </div>
          }
          isOpen={true}
          cancelText={<TranslatedMessage id="common.no" />}
          acceptText={<TranslatedMessage id="common.yes" />}
          onCancel={() => setShowCancelDialog(false)}
          onAccept={handleCancelModule}
        ></CustomDraggableDialog>
      )}
      <ExamSessionCandidateModuleRebookDialog
        opened={openRebookDialog}
        accept={(rebookWithInvoice) => {
          setOpenRebookDialog(false);
          onRebookModule(module, rebookWithInvoice);
        }}
        decline={() => setOpenRebookDialog(false)}
      />
    </>
  );
};

export default ExamSessionCandidateModule;
